import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import {
  ActivatedRoute, Router,
} from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { ChampionshipApiService } from '../../services/championship-api/ChampionshipApiService';
import {
  ChampionshipDetails,
  EventPlayoffStage,
  EventSeasonType,
  TeamEventSummary,
} from '../../types/Event';
import { resolveTeamLogoBackground } from '../../utils/resolveTeamLogoUrl';

@Component({
  selector: 'app-championship-view-page',
  standalone: false,
  templateUrl: './championship-view-page.component.html',
  styleUrl: '../team-view-page/team-view-page.component.sass',
})
export class ChampionshipViewPageComponent implements OnInit {
  public championship: ChampionshipDetails | null = null;
  public loading = false;
  public errorMessage: string | null = null;

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private championshipApi: ChampionshipApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    const championshipSlug = this.route.snapshot.paramMap.get('championshipSlug');
    if (!championshipSlug) {
      this.errorMessage = 'Чемпионат не найден.';
      return;
    }

    await this.loadChampionship(championshipSlug);
  }

  public get soldTicketsTotal(): number {
    return this.championship?.events.reduce((total, event) => total + event.soldTicketsTotal, 0) ?? 0;
  }

  public get revenueTotal(): number {
    return this.championship?.events.reduce((total, event) => total + event.soldRevenue, 0) ?? 0;
  }

  public get mostTicketsEvent(): TeamEventSummary | null {
    return this.getBestEvent('tickets');
  }

  public get mostRevenueEvent(): TeamEventSummary | null {
    return this.getBestEvent('revenue');
  }

  public getLogoBackground(): string | null {
    return this.championship ? resolveTeamLogoBackground(this.championship.logoUrl) : null;
  }

  public async deleteChampionship(): Promise<void> {
    if (!this.championship || !window.confirm(`Удалить чемпионат "${this.championship.name}"?`)) {
      return;
    }

    try {
      const response = await this.championshipApi.deleteChampionship(this.championship.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.notificationService.notify('directory', 'Чемпионат удален', this.championship.name);
      await this.router.navigate([
        '/dashboard/championship',
      ]);
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось удалить чемпионат.';
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public formatDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public getEventName(event: TeamEventSummary): string {
    return `${event.homeTeam.name} - ${event.awayTeam.name}`;
  }

  public getSeasonTypeName(type: EventSeasonType | null): string {
    if (!type) {
      return 'Все фазы';
    }
    if (type === 'playoff') {
      return 'Плей-офф';
    }
    if (type === 'cup') {
      return 'Кубок';
    }
    if (type === 'special') {
      return 'Специальное событие';
    }

    return 'Регулярный сезон';
  }

  public getPlayoffStageName(stage: EventPlayoffStage): string {
    return stage === 'final' ? 'Финал' : stage;
  }

  private getBestEvent(metric: 'tickets' | 'revenue'): TeamEventSummary | null {
    const events = this.championship?.events ?? [];
    if (events.length === 0) {
      return null;
    }

    return events.reduce((best, event) => {
      const bestValue = metric === 'tickets' ? best.soldTicketsTotal : best.soldRevenue;
      const eventValue = metric === 'tickets' ? event.soldTicketsTotal : event.soldRevenue;
      return eventValue > bestValue ? event : best;
    });
  }

  private async loadChampionship(championshipId: string): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const response = await this.championshipApi.getChampionship(championshipId);
      const championship = this.unwrapResponse(response);
      if (!championship) {
        this.errorMessage = 'Чемпионат не найден.';
        return;
      }

      this.championship = championship;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось загрузить чемпионат.';
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }
}
